import { useState, useEffect, useCallback, useRef, useId, useMemo } from 'react';
import { useLocation } from '@reach/router';
import qs from 'qs';
import useScript from './use-script';
import useUserContext from './use-user-context';
import { isBrowser, sortQueryParams, debug } from '../utilities/misc';
import loadBookItV2 from './hubspot-bookit';
import countryMappings from '../context/country-mappings';
import { rem } from '../utilities/style';

if (isBrowser) {
  // HS errors if jQuery is not available so we're faking it
  window.jQuery = window.jQuery || function selectNode(nodeOrSelector) {
    if (typeof nodeOrSelector === 'string') {
      return document.querySelector(nodeOrSelector);
    }
    return nodeOrSelector;
  };
}

const getLocale = (language) => {
  switch (language) {
    case 'zh-Hans':
      return 'zh-cn';
    case 'zh-Hant':
      return 'zh-hk';
    case 'pt':
      return 'pt-br';
    default:
      return language;
  }
};

const useHubspot = ({ formId,
  defaults = [],
  hubspotFieldOverrides,
  copyField,
  formOverrides,
  ...props }) => {
  const innerFormRef = useRef(null);
  const mountedRef = useRef(false);
  const { language, country, abTests, cookieData, developerRegions } = useUserContext();
  const [scripLoaded] = useScript('https://js.hsforms.net/forms/v2.js');
  const [shouldCreateForm, setShouldCreateForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formLoaded, setFormLoaded] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const uniqueId = useId();
  const safeFormId = `hs-${uniqueId.replace(/:/g, '_')}`;
  const location = useLocation();

  const formRef = useCallback((node) => {
    if (node !== null) {
      node.setAttribute('id', safeFormId);
      setShouldCreateForm(true);
    }
  }, [safeFormId]);

  useEffect(() => {
    const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

    // Override utms for /referral/get-started for WWW-6491
    let utmSource = cookieData?.utm_source || cookieData?.web_source;
    let utmMedium = cookieData?.utm_medium || cookieData?.web_medium;
    let latestUtmSource = cookieData?.utm_source || cookieData?.web_source;
    let latestUtmMedium = cookieData?.utm_medium || cookieData?.web_medium;
    if (cookieData?.utm_campaign === 'saasquatch') {
      utmSource = 'Customer Referral';
      utmMedium = 'Customer Referral';
      latestUtmSource = 'Customer Referral';
      latestUtmMedium = 'Customer Referral';
    }

    // Mapping of form fields to their corresponding data
    const fieldMappings = [
      { name: 'utm_source', value: utmSource },
      { name: 'utm_medium', value: utmMedium },
      { name: 'latest_form_src_ref', value: qs.stringify({ ...cookieData, ...queryParams }, { sort: sortQueryParams }) },
      { name: 'latest_utm_campaign', value: cookieData?.utm_campaign },
      { name: 'latest_utm_content', value: cookieData?.utm_content },
      { name: 'latest_utm_device', value: cookieData?.utm_device },
      { name: 'latest_utm_medium', value: latestUtmMedium },
      { name: 'latest_utm_source', value: latestUtmSource },
      { name: 'latest_utm_term', value: cookieData?.utm_term },
      { name: 'gclid', value: cookieData?.gclid },
    ];

    // Go over each mapping and apply value to form field
    fieldMappings.forEach(({ name, value }) => {
      const element = innerFormRef.current?.querySelector(`input[name="${name}"]`);
      debug(`${name}: ${value}`);
      // Set value and dispatch change event
      if (element && value !== undefined) {
        element.value = value;
        element.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }, [location.search, cookieData, defaults]);

  // Autofill for 'latest_abtest' field
  useEffect(() => {
    const latestAbTestElement = innerFormRef.current?.querySelector('input[name="latest_abtest"]');
    // Check if there are any AB tests on the page
    if (abTests.length > 0) {
      // Stringify the value
      const abTestValue = abTests ? abTests.join(',') : null;
      debug(`abTestValue = ${abTestValue}`);
      if (latestAbTestElement) {
        latestAbTestElement.value = abTestValue;
        latestAbTestElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }, [abTests, defaults]);

  // Countries that require consent from users
  const consentCountries = useMemo(() => ['au', 'ca', 'cn', 'gb', 'il', 'jp', 'tw'], []);

  useEffect(() => {
    const marketingConsentDiv = innerFormRef.current?.querySelector('.hs_marketing_consent_check');
    const marketingConsentCheckEl = innerFormRef.current?.querySelector('input[name="marketing_consent_check"]');
    const marketingConsentLabel = innerFormRef.current?.querySelector('.hs-form-booleancheckbox-display');
    const consentDisclaimer = innerFormRef.current?.querySelector('.legal-consent-container .hs-richtext');

    if (marketingConsentDiv && marketingConsentCheckEl) {
      if (!(developerRegions.includes('eu') || consentCountries.includes(country))) {
        marketingConsentDiv.style.display = 'none'; // Hide consent checkbox
        marketingConsentCheckEl.checked = true; // Auto-check marketing consent
        // Simulate a 'change' event, allowing other listeners to react
        marketingConsentCheckEl.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        Object.assign(marketingConsentLabel.style, {
          color: '#52697F',
          fontSize: rem(12),
        });
        consentDisclaimer.style.display = 'none'; // Hide disclaimer
      }
    }
  }, [developerRegions, country, consentCountries, defaults]);

  // Autofill country based on user's location
  useEffect(() => {
    // Autofill for fields with name 'country'
    const countryElement = innerFormRef.current?.querySelector('[name="country"]');
    if (countryElement && country) {
      // Look up country name by country code
      const countryName = countryMappings[country.toUpperCase()];
      if (countryElement.value === 'Please Select' || !countryElement.value) {
        // Check if we have a mapping for the given country
        if (countryName) {
          countryElement.value = countryName; // Use the country code from the mappings
          if (countryElement.type !== 'hidden') {
            // Simulate a 'change' event, allowing other listeners to react
            countryElement.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }
    // Autofill for fields with name 'countrycode'
    const countryCodeElement = innerFormRef.current?.querySelector('[name="countrycode"]');
    if (countryCodeElement && country) {
      if (countryCodeElement.value === 'Please Select' || !countryCodeElement.value) {
        countryCodeElement.value = country.toUpperCase();
        if (countryCodeElement.type !== 'hidden') {
          // Simulate a 'change' event, allowing other listeners to react
          countryCodeElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }, [country, defaults]);

  // Autofills selected field with hubspotFieldOverrides
  useEffect(() => {
    if (hubspotFieldOverrides) {
      let hubspotOverrides;
      // Check if hubspotFieldOverrides is a valid JSON-formatted string
      try {
        hubspotOverrides = JSON.parse(hubspotFieldOverrides);
      } catch (error) {
        // console.error('Error parsing hubspotFieldOverrides:', error);
        return; // Exit the useEffect callback if parsing fails
      }
      // Checks each JSON entry in the hubspotFieldOverrides and overrides each selected one
      hubspotOverrides.forEach((override) => {
        const selector = `select[name="${override.name}"]`;
        const formElement = innerFormRef.current?.querySelector(selector);
        if (formElement) {
          formElement.value = override.value;
          // Simulate a 'change' event, allowing other listeners to react
          formElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  });

  // Copies value from field A to field B
  // given that the respective checkbox is enabled
  const handleInputChange = useCallback((copyFieldEl, pasteFieldEl, checkboxFieldEl) => () => {
    if (checkboxFieldEl.checked) {
      /* eslint-disable-next-line no-param-reassign */
      pasteFieldEl.value = copyFieldEl.value;
      pasteFieldEl.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, []);
  // Compute the category just before calling useEffect
  const inputChange = handleInputChange();

  useEffect(() => {
    if (copyField && innerFormRef.current) {
      let fieldMappings;
      try {
        fieldMappings = JSON.parse(copyField);
      } catch (error) {
        // console.error('Error parsing JSON string:', error);
        return undefined;
      }
      const cleanupFunctions = [];
      fieldMappings.forEach((mapping) => {
        const { fieldToCopy, fieldToPaste, checkbox } = mapping;

        const copyFieldEl = innerFormRef.current?.querySelector(`input[name="${fieldToCopy}"]`);
        const pasteFieldEl = innerFormRef.current?.querySelector(`input[name="${fieldToPaste}"]`);
        const checkboxFieldEl = innerFormRef.current?.querySelector(`input[name="${checkbox}"]`);

        if (copyFieldEl && pasteFieldEl && checkboxFieldEl) {
          const onInputChange = handleInputChange(copyFieldEl, pasteFieldEl, checkboxFieldEl);
          copyFieldEl.addEventListener('input', onInputChange);
          checkboxFieldEl.addEventListener('change', onInputChange);

          cleanupFunctions.push(() => {
            copyFieldEl.removeEventListener('input', onInputChange);
            checkboxFieldEl.removeEventListener('change', onInputChange);
          });
        }
      });
      return () => cleanupFunctions.forEach((cleanup) => cleanup());
    }
    return undefined;
  }, [copyField, inputChange, handleInputChange]);

  const slugstoValues = {
    'remote-access': 'Business Access Product',
    'remote-support': 'SOS Product',
    enterprise: 'Enterprise',
    'on-prem': 'On-Prem',
    aem: 'Autonomous Endpoint Management',
    education: 'Education Solutions',
    foxpass: 'Foxpass',
  };

  // If the URL is /schedule-a-demo/[slugstoValue-key]
  // then return the value associated with the key for the form field
  const purposeOfDemo = (pathname) => {
    const slug = pathname?.split('schedule-a-demo/')?.[1];
    return slugstoValues?.[slug] || '';
  };

  // Autofill purpose of demo based on user's URL
  useEffect(() => {
    const demoElement = innerFormRef.current?.querySelector('select[name="purpose_of_demo"]');
    if (demoElement) {
      if (demoElement.value === 'Please Select' || !demoElement.value) {
        demoElement.value = purposeOfDemo(location.pathname);
        demoElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  useEffect(() => {
    if (innerFormRef.current !== null) {
      // Note: this doesn't handle resetting old values
      defaults.forEach((field) => {
        const el = innerFormRef.current?.querySelector(`[name="${field.name}"]`);
        if (el !== null) {
          el.setAttribute(field.attr, field.value);
        }
      });
    }
  }, [defaults]);

  useEffect(() => {
    mountedRef.current = true;

    if (isBrowser
        && scripLoaded
        && !formLoaded
        && !formLoading
        && shouldCreateForm
        && window.hbspt
    ) {
      setFormLoading(true);

      window.hbspt.forms.create({
        ...props,
        target: `#${safeFormId}`,
        locale: getLocale(language),
        formId,
        onFormReady(node) {
          innerFormRef.current = node;
          let nodeName = 'New Prospect';

          const formNodeNames = {
            'form/sdr-meeting': 'sdr demo form',
            'form/event-demo-booking': 'event demo form'
          };

          // Check if the current page is a "Schedule-a-Demo" page
          if (location.pathname.includes('form/schedule-a-demo')) {
            // Define mapping for specific demo types to their corresponding node names on BookIt
            const scheduleDemoNodeNames = {
              // antivirus: 'Antivirus',
              // 'augmented-reality': 'Augmented Reality',
              'remote-access': 'Splashtop Remote Access',
              'remote-support': 'Splashtop Remote Support',
              'on-prem': 'Enterprise On-Prem',
              enterprise: 'Splashtop Enterprise',
              education: 'Education',
              aem: 'Autonomous Endpoint Management',
              foxpass: 'Foxpass'
            };
            // Loop through the mapping and find a match in the current URL based on the key
            Object.keys(scheduleDemoNodeNames).forEach((key) => {
              if (location.pathname.includes(key)) {
                // Assign the matching demo type to nodeName
                nodeName = scheduleDemoNodeNames[key];
              }
            });
            // Call the loadBookItV2 function
            loadBookItV2(formId, node, nodeName);
          } else {
            // Handle forms that are not "Schedule-a-Demo" pages
            Object.keys(formNodeNames).some((key) => {
              if (location.pathname.includes(key)) {
                nodeName = formNodeNames[key]; // Assign the matching form type to nodeName
                loadBookItV2(formId, node, nodeName); // Call the loadBookItV2 function
                return true; // Break the loop once a match is found
              }
              return false; // Continue the loop if no match
            });
          }
          // Timeout necessary to get HS to render the field with the updated value
          setTimeout(() => {
            defaults.forEach((field) => {
              const el = node.querySelector(`[name="${field.name}"]`);
              if (el !== null) {
                el.value = field.value;
              }
            });
          });

          if (mountedRef.current) {
            setFormLoaded(true);
            setFormLoading(false);
          }
        },
        onFormSubmit() {
          if (mountedRef.current) {
            const marketingConsentDiv = innerFormRef.current?.querySelector('.hs_marketing_consent_check');
            const marketingConsentCheckEl = innerFormRef.current?.querySelector('input[name="marketing_consent_check"]');
            if (marketingConsentDiv && marketingConsentCheckEl && !(developerRegions.includes('eu') || consentCountries.includes(country))) {
              marketingConsentDiv.style.display = 'none'; // Hiding marketing consent
              marketingConsentCheckEl.checked = true; // Auto-checking marketing consent
            }
            setFormSubmitted(true);
          }
        },
        onFormSubmitted() {
          loadBookItV2.submit(); // V2 addition
        }
      });
    }

    return () => {
      mountedRef.current = false;
    };
  }, [
    props,
    scripLoaded,
    shouldCreateForm,
    formLoading,
    formLoaded,
    defaults,
    language,
    country,
    developerRegions,
    consentCountries,
    safeFormId,
    formId,
    location.pathname,
  ]);

  return { loaded: formLoaded, submitted: formSubmitted, formRef };
};

export default useHubspot;
